<template>
    <form @submit.prevent="submit" data-testid="tour-form">
        <div class="schedule-container-3col flex flex-wrap -mx-3">
            <!-- Left Column: Select Date -->
            <div class="schedule-column w-full md:w-1/2 lg:w-1/3 max-w-full px-3">
                <h3 class="schedule-section-title text-lg lg:text-[22px] text-[var(--charcoal)] font-bold mb-3">Select Date</h3>
                <div class="calendar-wrapper">
                    <CustomCalendar :model-value="formDate" @update:model-value="selectedDate" :form="form" />
                </div>
            </div>

            <!-- Middle Column: Available Times -->
            <div class="schedule-column w-full md:w-1/2 lg:w-[29.666666%] max-w-full px-3 max-[767px]:mb-3">
                <!-- Canyon Closure Message For Repair -->
                <div v-if="staticDateRange(formDate, form.tenant_id)">
                    <h3 class="schedule-section-title text-lg lg:text-[22px] text-[var(--charcoal)] font-bold mb-3">Available Times</h3>
                    <div class="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                        <p class="text-orange-700 font-bold text-lg">
                            <!-- Canyon is closed for repairs. Please select another day. -->
                            Please note: The canyon is currently closed for maintenance from January 12 to January 18. Tours will resume on January 19.
                        </p>
                    </div>
                </div>
                <div v-else-if="closureMessage">
                    <h3 class="schedule-section-title text-lg lg:text-[22px] text-[var(--charcoal)] font-bold mb-3">Available Times</h3>
                    <div class="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                        <div class="text-orange-700 font-bold text-lg" v-html="closureMessage"></div>
                    </div>
                </div>
                <div v-else-if="begins">
                    <h3 class="schedule-section-title text-lg lg:text-[22px] text-[var(--charcoal)] font-bold mb-3">Available Times</h3>
                    <div class="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                        <p class="text-orange-700 font-bold text-lg">Exciting News! Our Tour Begins on {{ begins }}.</p>
                    </div>
                </div>
                <div v-else-if="slotNotFound && dateTimeArr.length === 0">
                    <h3 class="schedule-section-title text-lg lg:text-[22px] text-[var(--charcoal)] font-bold mb-3">Available Times</h3>
                    <div class="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                        <p class="text-orange-700 font-bold text-lg">Apologies, No slots available on your chosen date.</p>
                    </div>
                </div>
                <div v-else-if="form.package_has_slots && !begins && !staticDateRange(formDate, form.tenant_id) && dateTimeArr.length > 0">
                    <h3 class="schedule-section-title text-lg lg:text-[22px] text-[var(--charcoal)] font-bold mb-3">Available Times</h3>
                    <div 
                        class="time-slots-list lg:max-h-[660px] overflow-x-auto"
                        ref="timeSlotsList"
                        @wheel="handleListWheel"
                        @scroll="handleInnerScroll"
                    >
                        <div v-for="slot in dateTimeArr" :key="slot.Id" 
                            class="time-slot-item border border-[var(--bone)] bg-white shadow-[0px_1.4px_2.8px_0px_#0000000D] rounded-2xl p-3 flex items-center justify-between mb-1 cursor-pointer" 
                            :class="{
                                'selected !border-[var(--primary)] !bg-[var(--primary-light)]': slot.Id === form.tour_slot_id,
                                'disabled opacity-50 pointer-events-none': isDisabled(slot) && !callToBookValidation(slot, true),
                                'sold-out opacity-50 pointer-events-none': !callToBookValidation(slot, false) && !callToBookValidation(slot, true)
                            }" 
                            v-show="slot.visible" 
                            @click="callToBookValidation(slot, true) ? openPhonePopup(slot) : selectedSlot(slot.Id, slot.Time, slot.slot_time)"
                            :data-testid="`time-slot-${slot.Id}`">
                            <div class="time-slot-left flex items-center">
                                <span class="time-icon mr-2.5"><svg width="17" height="27" viewBox="0 0 17 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8.53516 16.3828C12.4076 16.3828 15.5469 13.2436 15.5469 9.37109C15.5469 5.49863 12.4076 2.35938 8.53516 2.35938C4.66269 2.35938 1.52344 5.49863 1.52344 9.37109C1.52344 13.2436 4.66269 16.3828 8.53516 16.3828Z" stroke="#988881" stroke-width="1.40234" stroke-linecap="round" stroke-linejoin="round"/><path d="M8.53516 5.16406V9.37109L11.3398 10.7734" stroke="#988881" stroke-width="1.40234" stroke-linecap="round" stroke-linejoin="round"/></svg></span>
                                <span class="time-text text-lg text-[var(--charcoal-dark)] font-semibold">{{ slot.Time }}</span>
                            </div>
                            <div class="time-slot-right bg-[var(--light-beige)] text-base text-[var(--dark-brownish-gray)] font-semibold px-4 py-1 rounded-full flex items-center">
                                <span v-if="callToBookValidation(slot, false) && form.show_seat_availability" class="seats-badge">
                                    {{ slot.seats - slot.dd }} left
                                </span>
                                <span v-else-if="callToBookValidation(slot, true)" class="call-badge">
                                    CALL TO BOOK
                                </span>
                                <span v-else-if="form.show_seat_availability" class="soldout-badge">
                                    <span v-if="staticDateRange(formDate, form.tenant_id)">CLOSED</span>
                                    <span v-else>Sold Out</span>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Right Column: Booking Summary -->
            <div class="schedule-column w-full lg:w-[37%] max-w-full px-3 max-[1023px]:mt-3">
                <h3 class="schedule-section-title text-lg lg:text-[22px] text-[var(--charcoal)] font-bold mb-3">Booking Summary</h3>
                <div class="booking-summary-content">
                    <div class="bg-[linear-gradient(111.97deg,_#F4ECE1_0%,_#F5F3F0_100%)] rounded-2xl shadow-[0px_1.4px_2.8px_0px_#0000000D] mb-6"
                        :class="{
                            'border border-[var(--bone)]': (tourPackageData && tourPackageData.length > 0) || (formDate && form.time_date)
                        }">
                        <!-- Tour Details Card -->
                        <div v-if="tourPackageData && tourPackageData.length > 0" class="tour-summary-card flex items-start p-4">
                            <div class="tour-summary-image bg-[var(--light-gray)] w-16 h-16 min-w-16 min-h-16 rounded mr-3 flex items-center justify-center overflow-hidden mt-1.5">
                                <img v-if="form.package_image" :src="form.package_image" :alt="form.package_name" class="w-full h-full object-cover object-center" />
                                <div v-else class="tour-summary-placeholder text-center text-[15px] text-[#9CA3AF] uppercase px-1">NO IMAGE</div>
                            </div>
                            <div class="tour-summary-info">
                                <h4 class="tour-summary-title text-lg text-[var(--charcoal-dark)] font-medium">{{ form.package_name || tourPackageName }}</h4>
                                <!-- <div class="tour-summary-badges">
                                    <span v-if="tourPackageData[0]?.type" class="tour-badge tour-badge-tag">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                            <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
                                            <line x1="7" y1="7" x2="7.01" y2="7"></line>
                                        </svg>
                                        {{ tourPackageData[0].type }}
                                    </span>
                                </div> -->
                                <div v-if="tourPackageData[0]?.short_description" class="tour-summary-description text-base text-[var(--tone-gray)]">
                                    {{ tourPackageData[0].short_description }}
                                </div>
                                <div v-else class="tour-summary-description text-base text-[var(--tone-gray)]">
                                    Experience the beauty and culture of this amazing tour. Select your date and time to begin your adventure.
                                </div>
                            </div>
                        </div>

                        <!-- Selected Details -->
                        <div v-if="formDate && form.time_date" :class="[
                            'selected-details p-4',
                            tourPackageData && tourPackageData.length > 0 ? 'border-t border-[var(--bone)]' : '']">
                            <div class="selected-detail-row flex items-center mb-1">
                                <span class="detail-icon mr-1"><svg width="18" height="17" viewBox="0 0 18 17" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.1449 2.58659H12.5605V2.00228C12.5605 1.84731 12.499 1.69869 12.3894 1.58911C12.2798 1.47953 12.1312 1.41797 11.9762 1.41797C11.8213 1.41797 11.6726 1.47953 11.5631 1.58911C11.4535 1.69869 11.3919 1.84731 11.3919 2.00228V2.58659H6.71745V2.00228C6.71745 1.84731 6.65589 1.69869 6.54631 1.58911C6.43673 1.47953 6.28811 1.41797 6.13314 1.41797C5.97817 1.41797 5.82955 1.47953 5.71997 1.58911C5.61039 1.69869 5.54883 1.84731 5.54883 2.00228V2.58659H4.96452C4.18996 2.58752 3.44739 2.89562 2.8997 3.44332C2.352 3.99101 2.0439 4.73358 2.04297 5.50814L2.04297 12.5199C2.0439 13.2944 2.352 14.037 2.8997 14.5847C3.44739 15.1324 4.18996 15.4405 4.96452 15.4414H13.1449C13.9194 15.4405 14.662 15.1324 15.2097 14.5847C15.7574 14.037 16.0655 13.2944 16.0664 12.5199V5.50814C16.0655 4.73358 15.7574 3.99101 15.2097 3.44332C14.662 2.89562 13.9194 2.58752 13.1449 2.58659ZM3.21159 5.50814C3.21159 5.04323 3.39627 4.59737 3.72501 4.26863C4.05375 3.93989 4.49961 3.75521 4.96452 3.75521H13.1449C13.6098 3.75521 14.0556 3.93989 14.3844 4.26863C14.7131 4.59737 14.8978 5.04323 14.8978 5.50814V6.09245H3.21159V5.50814ZM13.1449 14.2728H4.96452C4.49961 14.2728 4.05375 14.0881 3.72501 13.7594C3.39627 13.4306 3.21159 12.9848 3.21159 12.5199V7.26107H14.8978V12.5199C14.8978 12.9848 14.7131 13.4306 14.3844 13.7594C14.0556 14.0881 13.6098 14.2728 13.1449 14.2728Z" fill="#988881" stroke="#988881" stroke-width="0.140234"/></svg></span>
                                <div class="flex items-center text-base text-[var(--charcoal-dark)]">
                                    <div class="detail-label mr-1 font-bold">Date:</div>
                                    <div class="detail-value">{{ formatDate(formDate) }}</div>
                                </div>
                            </div>
                            <div class="selected-detail-row flex items-center mb-1">
                                <span class="detail-icon mr-1"><svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9.05469 15.6484C12.9272 15.6484 16.0664 12.5092 16.0664 8.63672C16.0664 4.76425 12.9272 1.625 9.05469 1.625C5.18222 1.625 2.04297 4.76425 2.04297 8.63672C2.04297 12.5092 5.18222 15.6484 9.05469 15.6484Z" stroke="#988881" stroke-width="1.40234" stroke-linecap="round" stroke-linejoin="round"/><path d="M9.05469 4.42969V8.63672L11.8594 10.0391" stroke="#988881" stroke-width="1.40234" stroke-linecap="round" stroke-linejoin="round"/></svg></span>
                                <div class="flex items-center text-base text-[var(--charcoal-dark)]">
                                    <div class="detail-label mr-1 font-bold">Time:</div>
                                    <div class="detail-value">{{ form.time_date }}</div>
                                </div>
                            </div>
                            <div class="selected-detail-row flex items-center mb-1">
                                <span class="detail-icon mr-1"><svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11.8594 15.1543V13.752C11.8594 13.0081 11.5639 12.2947 11.0379 11.7687C10.5119 11.2428 9.79854 10.9473 9.05469 10.9473H4.84766C4.10381 10.9473 3.39042 11.2428 2.86444 11.7687C2.33846 12.2947 2.04297 13.0081 2.04297 13.752V15.1543" stroke="#988881" stroke-width="1.40234" stroke-linecap="round" stroke-linejoin="round"/><path d="M6.95117 8.14258C8.50016 8.14258 9.75586 6.88688 9.75586 5.33789C9.75586 3.7889 8.50016 2.5332 6.95117 2.5332C5.40219 2.5332 4.14648 3.7889 4.14648 5.33789C4.14648 6.88688 5.40219 8.14258 6.95117 8.14258Z" stroke="#988881" stroke-width="1.40234" stroke-linecap="round" stroke-linejoin="round"/><path d="M16.0664 15.1564V13.7541C16.0659 13.1326 15.8591 12.529 15.4784 12.0378C15.0976 11.5467 14.5646 11.1959 13.9629 11.0405" stroke="#988881" stroke-width="1.40234" stroke-linecap="round" stroke-linejoin="round"/><path d="M11.8594 2.62646C12.4627 2.78093 12.9974 3.1318 13.3793 3.62375C13.7611 4.1157 13.9684 4.72075 13.9684 5.34351C13.9684 5.96627 13.7611 6.57131 13.3793 7.06326C12.9974 7.55521 12.4627 7.90608 11.8594 8.06055" stroke="#988881" stroke-width="1.40234" stroke-linecap="round" stroke-linejoin="round"/></svg></span>
                                <div class="flex items-center text-base text-[var(--charcoal-dark)]">
                                    <div class="detail-label mr-1 font-bold">Guest:</div>
                                    <div class="detail-value">{{ form.total_people_selected }} {{ form?.type === 'Watercraft' ? 'Kayak' : 'People' }}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-if="reveal && tourPackageName" class="guest-selection-summary">
                        <div v-if="is_group_rate_enabled" class="guest-select-group flex items-start justify-between mb-4">
                            <div class="guest-info">
                                <span class="guest-label text-lg text-[var(--charcoal-dark)] font-medium block">Guests</span>
                                <span v-if="!isApmTenant" class="guest-price text-base text-[var(--tone-gray)]">Navajo Nation Tax: <span class="text-base font-medium bg-teal-600/20 text-neutral-900 rounded-full px-3 py-1 inline-block ml-2">{{ currencyFormat(form.selectedTax) }}</span></span>
                            </div>
                            <div class="relative">
                                <select class="guest-select ml-2 h-9 min-h-9 py-1 pl-3 pr-10 border border-[var(--bone)] rounded-full bg-white text-base text-[var(--charcoal-dark)] font-semibold overflow-hidden appearance-none" v-model.number="form.selectedSize" @change="handleGroupRateDiscountChange" data-testid="group-rate-select">
                                    <option v-for="group in tourPackageRateGroups" :value="group.size" :key="group.size">
                                        {{ group.size }} - {{ currencyFormat(displayRate(group)) }}
                                    </option>
                                </select>
                                <div class="select-icon w-4 h-2 absolute right-3 top-1/2 -translate-y-1/2">
                                    <svg width="14" height="8" viewBox="0 0 14 8" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1.01837 1.01843L6.87417 6.87424L12.73 1.01843" stroke="#919191" stroke-width="2.0368" stroke-linecap="round" stroke-linejoin="round"/></svg>
                                </div >
                            </div>
                        </div>

                        <div v-else class="guest-types-list">
                            <div v-for="tour in tourPackageRateGroups" :key="tour.id" class="guest-type-row flex items-center justify-between mb-4">
                                <div class="guest-type-info flex items-start sm:items-center max-[575px]:flex-col justify-between w-full">
                                    <div class="guest-type-name text-lg text-[var(--charcoal-dark)] font-medium">{{ tour.rate_for }} <span v-if="tour.description">({{ tour.description }})</span></div>
                                    <div class="guest-type-price text-base font-medium bg-teal-600/20 text-neutral-900 rounded-full sm:ml-3 px-3 py-1 inline-block">{{ currencyFormat(displayRate(tour)) }}</div>
                                </div>
                                <div class="guest-type-counter flex items-center ml-2">
                                    <button type="button" class="counter-btn-small w-8 h-8 min-h-8 min-w-8 sm:w-11 sm:h-11 sm:min-h-11 sm:min-w-11 border border-[var(--bone)] rounded-xl bg-[var(--primary-light)] hover:bg-white text-lg font-semibold" @click="decrement(tour.id)" aria-label="Decrement">-</button>
                                    <div class="counter-display mx-2 p-1 w-8 h-8 min-h-8 min-w-8 sm:w-11 sm:h-11 sm:min-h-11 sm:min-w-11 border border-[var(--primary)] rounded-xl bg-[var(--primary-light)] text-lg font-semibold flex items-center justify-center overflow-hidden">{{ form.counters[tour.id] ?? 0 }}</div>
                                    <button type="button" 
                                            class="counter-btn-small w-8 h-8 min-h-8 min-w-8 sm:w-11 sm:h-11 sm:min-h-11 sm:min-w-11 border border-[var(--bone)] rounded-xl text-lg font-semibold" 
                                            :class="isInfantIncrementDisabled(tour) ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-[var(--primary-light)] hover:bg-white'"
                                            :disabled="isInfantIncrementDisabled(tour)"
                                            @click="increment(tour.id)" 
                                            aria-label="Increment">+</button>
                                    <input type="hidden" :name="`people_group_${tour.id}`" :id="`people-group-${tour.id}`" :value="form.counters[tour.id] ?? 0" :data-testid="`people-group-${tour.id}`" />
                                    <select class="form-select people-group1 hidden ml-2 h-11 min-h-11 p-1 border border-[var(--primary)] rounded-xl bg-white text-lg font-semibold overflow-hidden" :name="`people_group${tour.id}`" :id="`people_group${tour.id}`" v-model="form.counters[tour.id]" hidden>
                                        <option v-for="item in selectgrouppeoples" :value="item.value" :key="item.value">{{ item.number }}</option>
                                    </select>
                                </div>

                                <div v-if="has_contacts" class="pax-container-summary">
                                    <div v-for="(pax, index) in form.paxDetails[tour.id] || []" :key="index" class="pax-card-small">
                                        <div class="pax-header-small">
                                            <span>Attendee {{ index + 1 }}</span>
                                            <button type="button" class="remove-pax-small" @click="removePax(tour.id, index)" aria-label="Remove Attendee">✖</button>
                                        </div>
                                        <div class="pax-body-small">
                                            <input type="text" :id="`name-${tour.id}-${index}`" v-model="pax.name" class="pax-input-small" placeholder="Name *" required />
                                            <input type="email" :id="`email-${tour.id}-${index}`" v-model="pax.email" class="pax-input-small" placeholder="Email *" required />
                                            <input type="tel" :id="`phone-${tour.id}-${index}`" v-model="pax.phone" class="pax-input-small" placeholder="Phone *" required />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-if="reveal && hotels.length" class="hotel-pickup-summary">
                        <h4 class="summary-subsection-title">Select Pickup Hotel</h4>
                        <Pickup :hotels="hotels" :form="form" />
                    </div>

                    <div ref="packageErrorDisplay" v-if="errors.length" class="error-summary text-base text-[var(--charcoal-dark)] mb-6 bg-[var(--primary-light)] rounded-2xl border border-[var(--bone)] shadow-[0px_1.4px_2.8px_0px_#0000000D] p-4">
                        <p><b>Please correct the following error(s):</b></p>
                        <ul class="following-error">
                            <li v-for="(error, idx) in errors" :key="`error-${idx}`" class="text-danger text-red-600">
                                {{ error }}
                            </li>
                        </ul>
                    </div>

                    <!-- Pricing Breakdown -->
                    <div v-if="form.subtotal > 0" class="pricing-breakdown bg-white rounded-2xl mb-6 border border-[var(--bone)] shadow-[0px_1.4px_2.8px_0px_#0000000D] p-4">
                        <template v-if="!isApmTenant">
                            <div class="pricing-row flex items-center justify-between text-base text-[var(--charcoal-dark)]">
                                <span>Subtotal ({{ form.total_people_selected }}x)</span>
                                <span>{{ currencyFormat(form.subtotal) }}</span>
                            </div>
                            <div class="pricing-row flex items-center justify-between text-base text-[var(--tone-gray)] mb-2">
                                <span>Taxes & Fees</span>
                                <span>{{ currencyFormat(form.fees) }}</span>
                            </div>
                        </template>
                        <div class="pricing-total text-xl text-[var(--charcoal-dark)] font-bold flex justify-between"
                            :class="{ 'border-t border-[var(--bone)] pt-2': !isApmTenant }">
                            <span>Total</span>
                            <span>{{ currencyFormat(isApmTenant ? form.subtotal : form.total) }}</span>
                        </div>
                    </div>

                    <div class="summary-totals mt-3">
                        <button type="submit" class="btn-continue-summary btn-primary w-full">Continue →</button>
                    </div>
                </div>
            </div>
        </div>
    </form>
</template>

<script>
import { formatCurrencyIntl } from '../../utils/currency';
import { getMomentTimezone } from '../../utils/dateUtils';
import Pickup from './../Hotel/Pickup.vue';
import CustomCalendar from './CustomCalendar.vue';

export default {
    name: 'TourForm',
    components: {
        CustomCalendar,
        Pickup
    },
    props: [
        'form',
        'formDate',
        'begins',
        'slotNotFound',
        'dateTimeArr',
        'reveal',
        'tourPackageName',
        'tourPackageData',
        'hotels',
        'errors',
        'is_group_rate_enabled',
        'is_partner_package',
        'tourPackageRateGroups',
        'selectgrouppeoples',
        'has_contacts',
        'staticDateRange',
        'selectedDate',
        'callToBookValidation',
        'openPhonePopup',
        'selectedSlot',
        'isDisabled',
        'handleGroupRateDiscountChange',
        'increment',
        'decrement',
        'updatePaxDetail',
        'removePax',
        'submit'
    ],
    computed: {
        isApmTenant() {
            return this.form.tenant_id === 'apm';
        },

        closureMessage() {
            // Only apply if tenant is 'apm'
            if (!this.isApmTenant) return null;

            const date = this.formDate;
            const packageId = Number(this.form.package_id);
            if (!date || !packageId) return null;

            const momentDate = this.parseDate(date); // We'll define parseDate below
            const year = momentDate.year();

            // Only apply in 2026
            if (year !== 2026) return null;

            // === Package ID 1: Antelope Boat Tour - Holiday Closures ===
            if (packageId === 1) {
                const holidays = [
                    '2026-01-01',
                    '2026-11-26',
                    '2026-12-24',
                    '2026-12-25'
                ];
                if (holidays.includes(date)) {
                    return 'We are closed for the holiday on this date.';
                }
            }

            // === Package ID 2: Navajo Boat - Seasonal Messages ===
            if (packageId === 2) {
                // const startOfYear = this.parseDate('2025-10-28');
                const startOfYear = this.parseDate('2026-01-01');
                const preSeasonEnd = this.parseDate('2026-03-09');

                if (momentDate.isBetween(startOfYear, preSeasonEnd, null, '[]')) {
                    return 'Exciting News! Our Tour Begins on <br />Tuesday, March 10, 2026.';
                }
            }

            return null;
        },
    },
    data() {
        return {
            mouseX: 0,
            mouseY: 0,
            isInside: false,
            lastPageScrollY: window.scrollY
        };
    },

    mounted() {
        window.addEventListener("mousemove", this.trackMouse);
        window.addEventListener("scroll", this.handlePageScroll, { passive: true });
    },

    beforeUnmount() {
        window.removeEventListener("mousemove", this.trackMouse);
        window.removeEventListener("scroll", this.handlePageScroll);
    },

    methods: {
        trackMouse(e) {
            this.mouseX = e.clientX;
            this.mouseY = e.clientY;
        },

        isInfantIncrementDisabled(rateGroup) {
            // If this is not an infant rate group, never disable
            if (rateGroup.is_infant_rate !== 1) {
                return false;
            }

            // For infant rate groups, disable if no non-infant rate groups have people selected
            const hasNonInfantSelection = this.tourPackageRateGroups.some(group => {
                return group.is_infant_rate !== 1 && (this.form.counters[group.id] || 0) > 0;
            });

            return !hasNonInfantSelection;
        },

        handlePageScroll() {
            const el = this.$refs.timeSlotsList;
            if (!el) return;

            const rect = el.getBoundingClientRect();
            const isNowInside =
                this.mouseX >= rect.left &&
                this.mouseX <= rect.right &&
                this.mouseY >= rect.top &&
                this.mouseY <= rect.bottom;

            if (isNowInside) {
                const pageScrollDiff = window.scrollY - this.lastPageScrollY;

                if (pageScrollDiff > 0) {
                    if (el.scrollTop < el.scrollHeight - el.clientHeight) {
                        el.scrollTop += pageScrollDiff;
                    }
                } else if (pageScrollDiff < 0) {
                    if (el.scrollTop > 0) {
                        el.scrollTop += pageScrollDiff;
                    }
                }
            }

            this.lastPageScrollY = window.scrollY;
        },

        handleListWheel(event) {
            const el = this.$refs.timeSlotsList;
            if (!el) return;

            const isScrollingDown = event.deltaY > 0;
            const isScrollingUp = event.deltaY < 0;

            const atTop = el.scrollTop === 0;
            const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight;

            // Case 1: inner can scroll → block page
            if ((isScrollingDown && !atBottom) || (isScrollingUp && !atTop)) {
                event.preventDefault();
                el.scrollTop += event.deltaY;
                return;
            }
        },
        parseDate(date) {
            return getMomentTimezone(this.$store.state.timezone, date);
        },
        currencyFormat(amount) {
            return formatCurrencyIntl(amount, this.$store.state.currency);
        },
        // APM shows the per-person ticket cost (rate + permit + tax + partner fee) instead of the
        // base rate; service fees are only shown at checkout. Mirrors Init.vue calculateTotals().
        displayRate(group) {
            if (!this.isApmTenant) return group.rate;

            const partnerFee = this.is_partner_package ? Number(group.partner_fee_amount || 0) : 0;
            const subtotal = Number(group.rate || 0) + Number(group.permit_fee || 0) + Number(group.tax || 0) + partnerFee;
            return Math.round(subtotal * 100) / 100;
        },
        formatDate(date) {
            return getMomentTimezone(this.$store.state.timezone, date).format('ddd, MMM D, YYYY');
        }
    }
};
</script>
